package navigation.settings.imagegallery

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.file.ui.essntlgallery.EssntlGalleryEvent
import com.essntl.features.file.ui.components.GalleryGrid
import com.essntl.features.file.ui.imageinfos.ImageInfosEvent
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import navigation.home.proposal.addeditproposal.AddEditProposalScreenNav
import navigation.settings.imagegallery.imageInfos.ImageInfosScreen
import navigation.settings.imagegallery.imageInfos.ImageInfosScreenModel
import navigation.utils.NavLinkScreen

data class ImageGalleryScreenNav(
    private val onRefresh: (() -> Unit) -> Unit,
) : NavLinkScreen {

    override val defaultTitle: String get() = "Image gallery"

    @Composable
    override fun Content() {
        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        val imageGalleryScreenModel = koinScreenModel<ImageGalleryScreenModel>()
        val state by imageGalleryScreenModel.state.collectAsState()

        val imageInfosScreenModel = koinScreenModel<ImageInfosScreenModel>()
        val imageInfosState by imageInfosScreenModel.state.collectAsState()

        LaunchedEffect(imageInfosState.deleteImage) {
            if (imageInfosState.deleteImage.isSuccess)
                imageInfosState.getFile.data?.let { image ->
                    imageGalleryScreenModel.onEvent(EssntlGalleryEvent.DeleteImage(image.id))
                    sidePanelState.sidePanelOpened = false
                }
        }

        LaunchedEffect(imageInfosState.updateImage) {
            if (imageInfosState.updateImage.isSuccess)
                imageInfosState.updateImage.data?.let { image ->
                    imageGalleryScreenModel.onEvent(EssntlGalleryEvent.UpdateImage(image))
                }
        }

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened)
                imageInfosScreenModel.onEvent(ImageInfosEvent.ClearState)
        }

        LaunchedEffect(state.selectedTabIndex) {
            imageGalleryScreenModel.onEvent(EssntlGalleryEvent.SwitchTab(index = state.selectedTabIndex))
            onRefresh {
                imageGalleryScreenModel.onEvent(EssntlGalleryEvent.SwitchTab(index = state.selectedTabIndex))
                sidePanelState.sidePanelOpened = false
            }
        }

        val navigator = LocalNavigator.current

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .padding(end = 1.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                            .background(color = MaterialTheme.esColorScheme.bgPrimary),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier =
                            Modifier
                                .background(
                                    color = MaterialTheme.esColorScheme.bgSecondary,
                                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                                )
                                .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
                                .padding(EsConstants.SectionHorizontalPadding)
                                .fillMaxWidth(),
                    ) {
                        EsMediumOutlineIconButton(
                            onClick = {
                                sidePanelState.sidePanelOpened = false
                            },
                            iconRes = EsIcons.ArrowRight,
                        )
                    }

                    ImageInfosScreen(
                        state = imageInfosState,
                        onEvent = imageInfosScreenModel::onEvent,
                        navigateToProposal = { id ->
                            navigator?.parent?.push(
                                AddEditProposalScreenNav(
                                    proposalId = id,
                                ),
                            )
                        },
                    )
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier = Modifier.width(1.spacing).fillMaxHeight(),
                        )
                    },
                ),
            modifier = Modifier.fillMaxSize(),
        ) {
            GalleryGrid(
                state = state,
                onEvent = imageGalleryScreenModel::onEvent,
                selectable = false,
                deletable = false,
                selectedImageIds = setOf(imageInfosState.getFile.data?.id.orEmpty()),
                onSwitchTab = { sidePanelState.sidePanelOpened = false },
                onClick = { _, imageId ->
                    if (imageId == imageInfosState.getFile.data?.id &&
                        sidePanelState.sidePanelOpened
                    ) return@GalleryGrid

                    sidePanelState.sidePanelOpened = true
                    imageInfosScreenModel.onEvent(
                        ImageInfosEvent.GetImage(
                            imageId,
                            state.selectedTabIndex,
                        ),
                    )
                },
                modifier = Modifier.fillMaxSize(),
            )
        }
    }
}